/*!
 * pitchDetect.js — the pitch of a stretch of sound, by autocorrelation
 * © 2026 Rob Canning — GPLv3
 *
 * adc.pitch, .note, .pitchConf (audioAnalysis.js). Pure: node tests it
 * with sines.
 *
 * The period is the first lag that correlates nearly as well as the best:
 * two or three periods are an integer number of samples more nearly than
 * one, so the best of all lags is often a subharmonic -- an octave or a
 * twelfth low (146.8 Hz read as 73.4).
 */

export const MIN_PITCH = 60;      // Hz: below, too low and noisy to trust
export const MAX_PITCH = 2000;    // Hz
const FIRST_PEAK = 0.9;           // the first peak within this much of the best is the period

/** Normalised autocorrelation of data at one lag. */
function correlationAt(data, lag) {
  let c = 0, n1 = 0, n2 = 0;
  for (let i = 0; i < data.length - lag; i++) {
    c += data[i] * data[i + lag];
    n1 += data[i] * data[i];
    n2 += data[i + lag] * data[i + lag];
  }
  const norm = Math.sqrt(n1 * n2);
  return norm > 0 ? c / norm : 0;
}

/**
 * The pitch of `data` (time domain, -1..1): { pitch: Hz, confidence: 0..1 },
 * pitch 0 when it is too quiet or nothing repeats.
 */
export function detectPitch(data, sampleRate) {
  const len = data.length;
  let rms = 0;
  for (let i = 0; i < len; i++) rms += data[i] * data[i];
  if (Math.sqrt(rms / len) < 0.01) return { pitch: 0, confidence: 0 };

  const minLag = Math.max(1, Math.floor(sampleRate / MAX_PITCH));
  const maxLag = Math.min(Math.floor(sampleRate / MIN_PITCH), Math.floor(len / 2) - 1);
  if (maxLag <= minLag + 1) return { pitch: 0, confidence: 0 };
  const corr = new Float64Array(maxLag + 2);
  let best = 0;
  for (let lag = minLag; lag <= maxLag + 1; lag++) {
    corr[lag] = correlationAt(data, lag);
    if (lag <= maxLag && corr[lag] > best) best = corr[lag];
  }
  // the first local maximum close to the best: the period, not a multiple of it
  let lag = 0;
  for (let l = minLag + 1; l <= maxLag; l++) {
    if (corr[l] >= FIRST_PEAK * best && corr[l] >= corr[l - 1] && corr[l] >= corr[l + 1]) { lag = l; break; }
  }
  if (!lag) return { pitch: 0, confidence: best };
  // parabolic interpolation between the lags either side
  const y1 = corr[lag - 1], y2 = corr[lag], y3 = corr[lag + 1];
  const bend = 2 * (2 * y2 - y1 - y3);
  const refined = bend ? lag + (y3 - y1) / bend : lag;
  return { pitch: sampleRate / refined, confidence: corr[lag] };
}
